import { AppShell as MantineAppShell, Text, Anchor, ActionIcon, Tooltip, Image, Group } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useComputedColorScheme } from '@mantine/core';
import { useScrolled } from '@/hooks/useScrolled';
import { useTheme } from '@/themes/useTheme';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconShield, IconWallet, IconHeart, IconUser, IconListTree, IconSpeakerphone } from '@tabler/icons-react';
import { MultiCartIcon } from './MultiCartIcon';
import { MessagesHeaderButton } from './MessagesHeaderButton';
import { Link, useNavigate } from 'react-router-dom';
import { SearchBar } from '@/components/ui/SearchBar';
import { useAuth, LoginModal, RegisterModal } from '@teletienda/shared-auth-ui';
import { useOAuthConfig } from '@/providers/PlatformBootstrapProvider';
import {
  HeaderLayout,
  HeaderPreferences,
  HeaderUserSection,
  HeaderNavButton,
  HeaderDivider,
} from '@teletienda/shared-ui-lib';
import { useLanguageSwitcher } from '@/i18n/useLanguageSwitcher';
import { useCurrencyChange } from '@/hooks/useCurrencyChange';
import { type SupportedLanguage } from '@/i18n/config';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores/uiStore';
import { useFavorites } from '@/hooks/useFavorites';
import { FavoritesDrawer } from '@/components/favorites';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [loginOpened, { open: openLogin, close: closeLogin }] = useDisclosure(false);
  const [registerOpened, { close: closeRegister }] = useDisclosure(false);
  const [logoError, setLogoError] = useState(false);
  const computedColorScheme = useComputedColorScheme('light');
  // useScrolled stays mounted always (cheap passive scroll listener); the
  // backdrop-blur visual reaction lives only under [data-tt-theme="editorial"]
  // CSS scope, so baseline ignores the data-scrolled attribute entirely.
  const scrolled = useScrolled(20);
  const { themeKey } = useTheme();
  const { user, logout } = useAuth();
  const { googleClientId: GOOGLE_CLIENT_ID, facebookAppId: FACEBOOK_APP_ID, telegramBotUsername: TELEGRAM_BOT_USERNAME } = useOAuthConfig();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');
  const { currentLanguage, changeLanguage } = useLanguageSwitcher();
  const { currentCurrency, changeCurrency } = useCurrencyChange();

  // UI Store for FavoritesDrawer
  const { openFavoritesDrawer } = useUIStore();

  // Favorites count for badge
  const { favorites } = useFavorites({ type: 'product' });
  const favoritesCount = favorites.length;

  // Check if user has admin role
  const isAdmin =
    user?.roles?.some(
      (role) => role === 'admin' || role === 'super_admin' || role === 'platform_admin'
    ) || false;

  // Switch between modals
  const switchToLogin = () => {
    closeRegister();
    openLogin();
  };

  // Handle login success with toast notification
  const handleLoginSuccess = useCallback(() => {
    closeLogin();
    notifications.show({
      title: tAuth('login.success_title'),
      message: tAuth('login.success_message'),
      color: 'green',
      autoClose: 3000,
    });
  }, [closeLogin, tAuth]);

  // Handle logout - navigate home first, then logout
  const handleLogout = useCallback(async () => {
    navigate('/');
    await logout();
    notifications.show({
      title: tAuth('userMenu.logout_success_title'),
      message: tAuth('userMenu.logout_success_message'),
      color: 'blue',
      autoClose: 3000,
    });
  }, [logout, navigate, tAuth]);

  // Handle registration complete (user is logged in)
  const handleRegisterComplete = useCallback(() => {
    closeRegister();
    notifications.show({
      title: tAuth('login.success_title'),
      message: tAuth('login.success_message'),
      color: 'green',
      autoClose: 3000,
    });
  }, [closeRegister, tAuth]);

  return (
    <MantineAppShell header={{ height: 70 }} padding="md">
      <a href="#main-content" className="skip-nav-link">
        {t('skip_to_content', 'Saltar al contenido')}
      </a>
      <MantineAppShell.Header
        aria-label={t('main_navigation', 'Navegación principal')}
        data-scrolled={scrolled || undefined}
      >
        <HeaderLayout testId="header">
          <HeaderLayout.Logo>
            {/* HNR-3: News megaphone sits next to the wordmark as a sibling of
                the logo anchor (platform news ≠ user actions; avoids nested <a>). */}
            <Group gap="xs" wrap="nowrap" align="center">
              <Anchor component={Link} to="/" style={{ textDecoration: 'none' }}>
                <Group gap="xs" wrap="nowrap" align="center">
                  {!logoError ? (
                    <Image
                      src={computedColorScheme === 'dark' ? '/images/logo-white.png' : '/images/logo-black.png'}
                      alt="TeleTienda"
                      h={36}
                      w="auto"
                      onError={() => setLogoError(true)}
                    />
                  ) : null}
                  {themeKey === 'editorial' ? (
                    <Text component="span" className="tt-logo-wordmark">
                      TeleTienda
                    </Text>
                  ) : (
                    <Text size="xl" fw={700} c="brand">
                      TeleTienda
                    </Text>
                  )}
                </Group>
              </Anchor>
              <HeaderNavButton
                icon={IconSpeakerphone}
                label={t('nav_news')}
                to="/news"
                testId="news-btn"
              />
            </Group>
          </HeaderLayout.Logo>

          <HeaderLayout.Center visibleFrom="sm">
            <SearchBar />
          </HeaderLayout.Center>

          <HeaderLayout.Actions>
            <HeaderPreferences
              currentLanguage={currentLanguage}
              onLanguageChange={(code) => changeLanguage(code as SupportedLanguage)}
              currentCurrency={currentCurrency}
              onCurrencyChange={changeCurrency}
              themeLabels={{ light: t('theme_light'), dark: t('theme_dark') }}
              languageTooltip={t('language')}
              currencyTooltip={t('currency')}
              testId="header-preferences"
            />

            {user ? (
              <>
                <HeaderDivider />

                <HeaderNavButton
                  icon={IconHeart}
                  label={t('favorites.title')}
                  onClick={openFavoritesDrawer}
                  badge={favoritesCount}
                  badgeColor="red"
                  testId="favorites-btn"
                />

                <MessagesHeaderButton />

                <MultiCartIcon />

                <HeaderDivider />

                <HeaderNavButton
                  icon={IconBuildingStore}
                  label={t('my_merchants')}
                  to="/my-merchants"
                  testId="my-merchants-btn"
                />

                <HeaderNavButton
                  icon={IconWallet}
                  label={t('finances')}
                  to="/finances"
                  testId="finances-btn"
                />

                {isAdmin && <HeaderDivider />}

                {isAdmin && (
                  <HeaderNavButton
                    icon={IconListTree}
                    label={t('catalog')}
                    to="/admin/catalog"
                    testId="catalog-btn"
                  />
                )}

                {isAdmin && (
                  <HeaderNavButton
                    icon={IconShield}
                    label={t('admin')}
                    to="/admin/users"
                    testId="admin-btn"
                  />
                )}

                <HeaderDivider />

                <HeaderUserSection
                  user={user}
                  onLogout={handleLogout}
                  profileTooltip={t('profile')}
                  logoutTooltip={t('logout')}
                  logoutConfirmTitle={tAuth('userMenu.logoutConfirmTitle')}
                  logoutConfirmMessage={tAuth('userMenu.logoutConfirmMessage')}
                  cancelLabel={tAuth('userMenu.cancel')}
                  confirmLabel={tAuth('userMenu.logout')}
                  testId="user-section"
                />
              </>
            ) : (
              <>
                <HeaderDivider />
                <Tooltip label={t('login')}>
                <ActionIcon
                  variant="filled"
                  size="lg"
                  onClick={openLogin}
                  aria-label={t('login')}
                  data-testid="login-btn"
                >
                  <IconUser size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              </>
            )}
          </HeaderLayout.Actions>
        </HeaderLayout>
      </MantineAppShell.Header>

      <MantineAppShell.Main>
        <main id="main-content" tabIndex={-1}>{children}</main>
      </MantineAppShell.Main>

      {/* Login Modal */}
      <LoginModal
        opened={loginOpened}
        onClose={closeLogin}
        onSuccess={handleLoginSuccess}
        showSocialLogin
        googleClientId={GOOGLE_CLIENT_ID}
        facebookAppId={FACEBOOK_APP_ID}
        telegramBotUsername={TELEGRAM_BOT_USERNAME}
      />

      {/* Register Modal */}
      <RegisterModal
        opened={registerOpened}
        onClose={closeRegister}
        onSuccess={handleRegisterComplete}
        showLogin
        onLoginClick={switchToLogin}
        termsUrl="/terms"
        privacyUrl="/privacy"
        showSocialLogin
        googleClientId={GOOGLE_CLIENT_ID}
        facebookAppId={FACEBOOK_APP_ID}
        telegramBotUsername={TELEGRAM_BOT_USERNAME}
      />

      {/* Favorites Drawer */}
      <FavoritesDrawer />
    </MantineAppShell>
  );
}
